import axios from 'axios';
import {
  Column,
  Gear,
  Grid,
  Lightning,
  PasswordInput,
  RadioInput,
  Text,
  TextInput,
  ToggleSwitch,
  Touchable,
  User,
  Wallet,
} from 'plaid-threads';
import Button from 'plaid-threads/Button';
import NumberInput from 'plaid-threads/NumberInput';
import Select from 'plaid-threads/Select';
import React, { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import Admin from '../Admin';
import { AppContext } from '../App';
import Consents from '../Consents';
import Header, { LanguageSelector, SingleCountryCodeSelector } from '../Header';
import HeroConsumer from '../assets/hero-home-consumer-right-us-en.gif';
import HeroAuth from '../assets/hero-product-auth-left-uk-en.gif';
import HeroUK from '../assets/hero-uk-en.gif';
import M2020 from '../assets/eu-hero-static.webp';
import { Currency } from '../constants';
import { getAllCurrenciesForCountry, getAmountString } from '../currencyUtils';
import EnrichData from '../EnrichData';
import { getRouteMapping, UserJourney } from '../routeConfig';
import './index.scss';

const ALL_ACCOUNT_PRODUCTS = [
  'auth',
  'identity',
  'assets',
  'income_verification',
  'investments',
  'transactions',
  'international_base_report',
  'international_income_insights',
];
const TRANSACTION_DAY_OPTIONS = [
  { label: '90 days', value: 90 },
  { label: '180 days', value: 180 },
  { label: '365 days', value: 365 },
  { label: '730 days', value: 730 },
];

interface Props {}

const LandingV2: React.FC<Props> = (props) => {
  const { dispatch, amount, countryCode, productExperience, accountProducts } =
    React.useContext(AppContext);

  const history = useHistory();
  const location = useLocation();

  const [email, setEmail] = React.useState('');
  const [authenticationError, setAuthenticationError] = React.useState('');
  const [amountError, setAmountError] = React.useState('');
  const [accountProductsMap, setAccountProductsMap] = React.useState<{
    [key: string]: boolean;
  }>(
    Object.fromEntries(
      ALL_ACCOUNT_PRODUCTS.map((productKey) => [
        productKey,
        accountProducts.indexOf(productKey) >= 0,
      ]),
    ),
  );

  const [userJourney, setUserJourney] = React.useState<UserJourney>(() => {
    return getRouteMapping(location.pathname).journey;
  });
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [transactionDays, setTransactionDays] = React.useState(90);

  React.useEffect(() => {
    const { product } = getRouteMapping(location.pathname);
    if (product && !productExperience) {
      dispatch({
        type: 'SET_PRODUCT_EXPERIENCE',
        payload: { productExperience: product },
      });
      if (product === 'PIS') {
        dispatch({ type: 'SET_SCHEME', payload: { scheme: 'LOCAL_DEFAULT' } });
      }
    }
    // Only run on mount — read URL once for initial product state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const unlisten = history.listen((loc) => {
      const { journey, product } = getRouteMapping(loc.pathname);
      setUserJourney(journey);
      if (product) {
        dispatch({
          type: 'SET_PRODUCT_EXPERIENCE',
          payload: { productExperience: product },
        });
        if (product === 'PIS') {
          dispatch({
            type: 'SET_SCHEME',
            payload: { scheme: 'LOCAL_DEFAULT' },
          });
        }
      }
    });
    return unlisten;
  }, [history, dispatch]);

  const getIsAdmin = React.useCallback(async () => {
    await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/is_admin`, {
        withCredentials: true,
      })
      .catch(function (error) {
        setIsAdmin(false);
      })
      .then(function (response) {
        if (response?.status === 200 && response.data.username !== '') {
          setIsAdmin(true);
          setEmail(`${response.data.username}@plaid.com`);
        } else {
          setIsAdmin(false);
        }
      });
  }, [setEmail]);

  React.useEffect(() => {
    getIsAdmin();
  }, [getIsAdmin]);

  const updateAccountProducts = (key: string) => {
    const oldValue = accountProductsMap[key];
    setAccountProductsMap({
      ...accountProductsMap,
      [key]: !oldValue,
    });
  };

  React.useEffect(() => {
    dispatch({
      type: 'SET_AMOUNT',
      payload: {
        amount: 0,
      },
    });
    // Don't reset productExperience if URL already set it
    const { product } = getRouteMapping(location.pathname);
    if (!product) {
      dispatch({
        type: 'SET_PRODUCT_EXPERIENCE',
        payload: { productExperience: '' },
      });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const accountProductList = useMemo(
    () =>
      Object.entries(accountProductsMap)
        .filter(([_productKey, isProductEnabled]) => isProductEnabled)
        .map(([productKey]) => productKey),
    [accountProductsMap],
  );

  const onSubmit = async (evt: any) => {
    evt.preventDefault();
    evt.stopPropagation();
    switch (productExperience) {
      case 'AIS':
        dispatch({
          type: 'SET_ACCOUNT_PRODUCTS',
          payload: {
            accountProducts: accountProductList,
          },
        });
        if (
          accountProductsMap['transactions'] ||
          accountProductsMap['income_verification'] ||
          accountProductsMap['assets'] ||
          accountProductsMap['international_base_report'] ||
          accountProductsMap['international_income_insights']
        ) {
          dispatch({
            type: 'SET_TRANSACTION_DAYS',
            payload: {
              transactionDays: transactionDays,
            },
          });
        }
        dispatch({
          type: 'SET_PAGE',
          payload: {
            page: 'aisV2',
          },
        });
        history.push({
          pathname: '/account/launch',
          search: location.search,
        });
        break;
      case 'PIS':
        // validate amount
        if (!amount || amount < 1 || amount > 5) {
          setAmountError('Please enter an amount between 1 and 5');
        } else {
          dispatch({
            type: 'SET_PAGE',
            payload: {
              page: 'pisV2',
            },
          });
          history.push({
            pathname: '/payment/launch',
            search: location.search,
          });
        }
        break;
    }
  };

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const key = params.get('key');
  const isM2020 = key === 'm2020';

  return (
    <div
      className={userJourney === 'welcome' ? '' : 'innerContainer'}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {isM2020 && <Header isM2020 />}
      <div className="LandingV2">
        {userJourney === 'welcome' && (
          <Welcome
            email={email}
            authenticationError={authenticationError}
            setAuthenticationError={setAuthenticationError}
            setEmail={setEmail}
            setUserJourney={setUserJourney}
            isAdmin={isAdmin}
            turnOffAdminView={() => {
              setIsAdmin(false);
              setEmail('');
            }}
          />
        )}
        {userJourney === 'product' && productExperience === 'PIS' && (
          <PIS
            amountError={amountError}
            onSubmit={onSubmit}
            isSubmitDisabled={
              countryCode.length < 1 ||
              !email ||
              !productExperience ||
              (productExperience === 'PIS' && !amount)
            }
          />
        )}
        {userJourney === 'product' && productExperience === 'AIS' && (
          <AIS
            accountProductsMap={accountProductsMap}
            updateAccountProducts={updateAccountProducts}
            onSubmit={onSubmit}
            isSubmitDisabled={
              countryCode.length < 1 ||
              !email ||
              !productExperience ||
              (productExperience === 'AIS' && accountProductList.length <= 0)
            }
            transactionDays={transactionDays}
            setTransactionDays={setTransactionDays}
          />
        )}
        {userJourney === 'admin' && <Admin isAdmin={isAdmin} />}
        {userJourney === 'enrich' && isAdmin && <EnrichData />}
      </div>
    </div>
  );
};

type AISProps = {
  accountProductsMap: any;
  updateAccountProducts: any;
  onSubmit: any;
  isSubmitDisabled: boolean;
  transactionDays: number;
  setTransactionDays: (days: number) => void;
};

const getProductDescription = (product: string): string => {
  switch (product) {
    case 'auth':
      return 'Account number, sort code, IBAN and BIC';
    case 'transactions':
      return 'Up to 2 years of categorised transaction data';
    case 'identity':
      return 'Bank account-holder information';
    case 'income_verification':
      return 'Verify Income streams, including recent and historical income';
    case 'investments':
      return 'Holdings, securities and investment transactions (Alpha — not yet generally available)';
    case 'assets':
      return 'Affordability report, with risk indicators (PDF available)';
    case 'international_base_report':
      return 'Comprehensive bank account and cash flow data for non-US markets';
    case 'international_income_insights':
      return 'Income insights and verification data for non-US markets';
  }
  return '';
};

const getProductLabel = (product: string): string => {
  switch (product) {
    case 'auth':
    case 'transactions':
    case 'identity':
      return product;
    case 'assets':
      return 'Financial Insights';
    case 'income_verification':
      return 'Income';
    case 'investments':
      return 'Investments (Alpha)';
    case 'international_base_report':
      return 'International Base Report';
    case 'international_income_insights':
      return 'International Income Insights';
  }
  return '';
};

const PFC_VERSION_OPTIONS = [
  { label: 'v2', value: 'v2' },
  { label: 'v1', value: 'v1' },
];

const AIS: React.FC<AISProps> = ({
  accountProductsMap,
  updateAccountProducts,
  isSubmitDisabled,
  onSubmit,
  transactionDays,
  setTransactionDays,
}) => {
  const { dispatch, accountType, pfcVersion } = React.useContext(AppContext);

  return (
    <div>
      <div>
        <div className="LandingV2__title">Connect your Account</div>
        <div>
          See for yourself what one integration for all of open banking can look
          like.
        </div>
      </div>
      <div className="LandingV2__instructions">Select Market(s)</div>
      <SingleCountryCodeSelector />
      <div className="LandingV2__instructions">Select Language</div>
      <LanguageSelector />
      <div className="LandingV2__instructions">
        Select Account Holder Category Filter (optional)
      </div>
      <div style={{ marginBottom: '10px' }}>
        <span style={{ fontSize: '12px', color: '#666' }}>
          Select an account holder category filter to enable a custom link
          experience.
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '2rem',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <RadioInput
          id="account-type-both"
          value={accountType === null || accountType === 'default'}
          name="account-type"
          onChange={() =>
            dispatch({
              type: 'SET_ACCOUNT_TYPE',
              payload: {
                accountType: null,
              },
            })
          }
        >
          All accounts
        </RadioInput>
        <RadioInput
          id="account-type-personal"
          value={accountType === 'personal'}
          name="account-type"
          onChange={() =>
            dispatch({
              type: 'SET_ACCOUNT_TYPE',
              payload: {
                accountType: 'personal',
              },
            })
          }
        >
          Personal only
        </RadioInput>
        <RadioInput
          id="account-type-business"
          value={accountType === 'business'}
          name="account-type"
          onChange={() =>
            dispatch({
              type: 'SET_ACCOUNT_TYPE',
              payload: {
                accountType: 'business',
              },
            })
          }
        >
          Business only
        </RadioInput>
      </div>
      <div className="LandingV2__instructions">Select Product(s)</div>
      <Grid fullWidth className="productsContainer">
        {ALL_ACCOUNT_PRODUCTS.map((productKey) => (
          <ToggleSwitch
            className="productToggle"
            key={productKey}
            id={productKey}
            value={accountProductsMap[productKey]}
            onChange={() => updateAccountProducts(productKey)}
          >
            <div>
              <Text font="mono" weight="bold" casing="capitalize">
                {getProductLabel(productKey)}
              </Text>
              <Text font="mono" weight="normal">
                {getProductDescription(productKey)}
              </Text>
            </div>
          </ToggleSwitch>
        ))}
      </Grid>
      {(accountProductsMap['transactions'] ||
        accountProductsMap['income_verification'] ||
        accountProductsMap['assets'] ||
        accountProductsMap['international_base_report'] ||
        accountProductsMap['international_income_insights']) && (
        <div style={{ marginTop: '1rem' }}>
          <div className="LandingV2__instructions">
            Select Transaction History Days
          </div>
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            {TRANSACTION_DAY_OPTIONS.map((option) => (
              <RadioInput
                key={option.value}
                id={`transaction-days-${option.value}`}
                value={transactionDays === option.value}
                name="transaction-days"
                onChange={() => setTransactionDays(option.value)}
              >
                {option.label}
              </RadioInput>
            ))}
          </div>
        </div>
      )}
      {accountProductsMap['transactions'] && (
        <div style={{ marginTop: '1rem' }}>
          <div className="LandingV2__instructions">Select PFC Version</div>
          <Select
            id="pfcVersion"
            label="PFC Version"
            options={PFC_VERSION_OPTIONS}
            value={PFC_VERSION_OPTIONS.find((opt) => opt.value === pfcVersion)}
            onChange={(option: { label: string; value: string }) =>
              dispatch({
                type: 'SET_PFC_VERSION',
                payload: { pfcVersion: option.value },
              })
            }
          />
        </div>
      )}
      <div className="LandingV2__button">
        <Button wide onClick={onSubmit} disabled={isSubmitDisabled}>
          Launch Plaid!
        </Button>
      </div>
      <img
        src={HeroAuth}
        width="100%"
        style={{ marginTop: -40 }}
        alt="decorative animation depicting a person verifying their account numbers on a screen"
      />
    </div>
  );
};

type PISProps = {
  amountError: string;
  onSubmit: any;
  isSubmitDisabled: boolean;
};

const PIS: React.FC<PISProps> = ({
  amountError,
  onSubmit,
  isSubmitDisabled,
}) => {
  const { dispatch, currency, countryCode, accountType } =
    React.useContext(AppContext);
  const [isVRP, setIsVRP] = React.useState(false);
  const [isVRPEligible, setIsVRPEligible] = React.useState(false);

  React.useEffect(() => {
    if (countryCode.filter((country) => country === 'GB').length > 0) {
      setIsVRPEligible(true);
    } else {
      setIsVRPEligible(false);
      setIsVRP(false);
    }
  }, [countryCode]);

  const allCurrenciesSupported = getAllCurrenciesForCountry(countryCode);
  const hasMultipleCurrencies = allCurrenciesSupported.length > 1;
  return (
    <div>
      <div>
        <div className="LandingV2__title">Make a payment</div>
        <div>
          See for yourself what one integration for all of open banking can look
          like.
        </div>
      </div>
      <div className="LandingV2__instructions">Select Market(s)</div>
      <SingleCountryCodeSelector />
      <div className="LandingV2__instructions">Select Language</div>
      <LanguageSelector />
      <div className="LandingV2__instructions">
        Enter the payment amount (max of {getAmountString(5, currency)})
      </div>
      <Grid fullWidth className="LandingV2__amount">
        <Column medium="auto" small={12}>
          <NumberInput
            id="amount"
            label="Payment amount"
            errorMessage={amountError}
            onChange={(e) =>
              dispatch({
                type: 'SET_AMOUNT',
                payload: {
                  amount: parseFloat(e.target.value),
                },
              })
            }
            placeholder="Payment amount"
          />
        </Column>
        {hasMultipleCurrencies && (
          <Column medium={3} small={12}>
            <Select
              menuPlacement="top"
              label="Currency"
              id="currency"
              placeholder="Select currency"
              options={allCurrenciesSupported.map(getSelectOptionsForCurrency)}
              value={getSelectOptionsForCurrency(currency)}
              onChange={(option: CurrencySelectOption) =>
                dispatch({
                  type: 'SET_CURRENCY',
                  payload: {
                    currency: option.value,
                  },
                })
              }
            />
          </Column>
        )}
      </Grid>
      <div className="LandingV2__instructions">
        Select Account Holder Category Filter (optional)
      </div>
      <div style={{ marginBottom: '10px' }}>
        <span style={{ fontSize: '12px', color: '#666' }}>
          Select an account holder category filter to enable a custom link
          experience.
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '2rem',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <RadioInput
          id="account-type-both-pis"
          value={accountType === null || accountType === 'default'}
          name="account-type-pis"
          onChange={() =>
            dispatch({
              type: 'SET_ACCOUNT_TYPE',
              payload: {
                accountType: null,
              },
            })
          }
        >
          All accounts
        </RadioInput>
        <RadioInput
          id="account-type-personal-pis"
          value={accountType === 'personal'}
          name="account-type-pis"
          onChange={() =>
            dispatch({
              type: 'SET_ACCOUNT_TYPE',
              payload: {
                accountType: 'personal',
              },
            })
          }
        >
          Personal only
        </RadioInput>
        <RadioInput
          id="account-type-business-pis"
          value={accountType === 'business'}
          name="account-type-pis"
          onChange={() =>
            dispatch({
              type: 'SET_ACCOUNT_TYPE',
              payload: {
                accountType: 'business',
              },
            })
          }
        >
          Business only
        </RadioInput>
      </div>
      {isVRPEligible && (
        <ToggleSwitch
          className="productToggle"
          key={'vrpToggle'}
          id={'vrpToggle'}
          value={isVRP}
          onChange={() => setIsVRP(!isVRP)}
          trueText="Make Variable recurring payment"
          falseText="Make Variable recurring payment"
        >
          <></>
        </ToggleSwitch>
      )}
      {isVRP && <Consents />}
      {!isVRP && (
        <div className="LandingV2__button">
          <Button wide onClick={onSubmit} disabled={isSubmitDisabled}>
            Launch Plaid!
          </Button>
        </div>
      )}
      <img
        src={HeroConsumer}
        width="100%"
        alt="descorative animation depicting a person using a fintech app on their phone while at the park with their dog"
      />
    </div>
  );
};

type WelcomeProps = {
  authenticationError: string;
  setAuthenticationError: React.Dispatch<React.SetStateAction<string>>;
  email: string;
  setEmail: any;
  setUserJourney: any;
  isAdmin: boolean;
  turnOffAdminView: () => void;
};

const Welcome: React.FC<WelcomeProps> = ({
  email,
  authenticationError,
  setAuthenticationError,
  setEmail,
  setUserJourney,
  isAdmin,
  turnOffAdminView,
}) => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const key = params.get('key');
  const timestamp = params.get('timestamp');
  const isV3 = !key && !timestamp;
  const isM2020 = key === 'm2020';

  const { dispatch } = React.useContext(AppContext);
  const history = useHistory();
  const location = useLocation();

  const [showPassword, setShowPassword] = React.useState<boolean>();

  const setProductExperience = async (
    evt: React.MouseEvent<Element, MouseEvent>,
    productExperience: 'AIS' | 'PIS',
  ) => {
    // Prevent browser from submitting the form.
    evt.preventDefault();
    evt.stopPropagation();

    await axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/login`,
        { email: email, key: key, timestamp: timestamp },
        { withCredentials: true },
      )
      .catch(function (error) {
        setAuthenticationError(error.response.data.message);
      })
      .then(function (response) {
        if (response?.status === 200) {
          setUserJourney('product');
          dispatch({
            type: 'SET_PRODUCT_EXPERIENCE',
            payload: {
              productExperience,
            },
          });
          if (productExperience === 'PIS') {
            dispatch({
              type: 'SET_SCHEME',
              payload: {
                scheme: 'LOCAL_DEFAULT',
              },
            });
          }
          history.push({
            pathname: productExperience === 'PIS' ? '/payment' : '/account',
            search: location.search,
          });
        }
      });
  };

  React.useEffect(() => {
    if (isM2020) {
      setEmail('m2020');
    }
  }, [isM2020, setEmail]);

  return (
    <div className="LandingV2__authentication">
      <div>
        <div className="LandingV2__titleWrapper">
          <Text appearance="h2" as="h1">
            Plaid Demo
          </Text>
          <Text size={20}>
            See how a single integration with Plaid can serve all of your open
            banking needs.
          </Text>
        </div>
        {isV3 && isAdmin ? (
          <div>
            <Text size={20}>
              {`Hello ${email.split('@')[0]}! `}
              <Touchable
                size={14}
                onClick={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  turnOffAdminView();
                }}
              >
                <span
                  style={{ textDecoration: 'underline' }}
                >{`(Switch to non-admin view)`}</span>
              </Touchable>
            </Text>
          </div>
        ) : isM2020 ? (
          <div>
            <Text size={20}>Welcome to Money20/20!</Text>
          </div>
        ) : (
          <div className="LandingV2__passwordWrapper">
            {isV3 ? (
              <TextInput
                id="email"
                label={'Enter an authorized email address'}
                errorMessage={authenticationError}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={'Email address'}
              />
            ) : (
              <PasswordInput
                id="email"
                label={'Authenticate with the Passphrase'}
                errorMessage={authenticationError}
                onChange={(e) => setEmail(e.target.value)}
                setShowPassword={() => setShowPassword(!showPassword)}
                showPassword={showPassword}
                placeholder={'Passphrase'}
              />
            )}
          </div>
        )}
        <div className="LandingV2__experienceWrapper">
          <div>
            <Button
              className="LandingV2__experienceWrapper__button"
              wide={isMobile}
              centered={true}
              inline
              disabled={!email && !isAdmin}
              onClick={(evt) => {
                setProductExperience(evt, 'AIS');
              }}
              icon={<User />}
            >
              Connect My Account
            </Button>
          </div>
          <div>
            <Button
              className="LandingV2__experienceWrapper__button"
              wide={isMobile}
              centered={true}
              inline
              disabled={!email && !isAdmin}
              onClick={(evt) => {
                setProductExperience(evt, 'PIS');
              }}
              icon={<Wallet />}
            >
              Make a Payment
            </Button>
          </div>
          {isV3 && isAdmin && (
            <div>
              <Button
                className="LandingV2__experienceWrapper__button"
                wide={isMobile}
                centered={true}
                inline
                onClick={async (evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();

                  try {
                    await axios.post(
                      `${process.env.REACT_APP_SERVER_URL}/login`,
                      { email: email, key: key, timestamp: timestamp },
                      { withCredentials: true },
                    );
                    setUserJourney('enrich');
                    history.push({
                      pathname: '/enrich',
                      search: location.search,
                    });
                  } catch (err: any) {
                    setAuthenticationError(
                      err.response?.data?.message || 'Authentication failed',
                    );
                  }
                }}
                icon={<Lightning />}
              >
                Enrich Sample Data
              </Button>
            </div>
          )}
          {isV3 && isAdmin && (
            <div>
              <Button
                className="LandingV2__experienceWrapper__button"
                wide={isMobile}
                centered={true}
                inline
                onClick={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  setUserJourney('admin');
                  history.push({
                    pathname: '/admin',
                    search: location.search,
                  });
                }}
                icon={<Gear />}
              >
                Admin
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className={isM2020 ? 'LandingV2__img__m2020' : 'LandingV2__img'}>
        {isM2020 ? (
          <img
            src={M2020}
            width="100%"
            alt="person with a suitcase in front of various European landmarks"
          />
        ) : (
          <img
            src={HeroUK}
            width="100%"
            alt="decorative animation depicting a person writing code in the UK"
          />
        )}
      </div>
    </div>
  );
};

export default LandingV2;

type CurrencySelectOption = { label: Currency; value: Currency };
function getSelectOptionsForCurrency(currency: Currency): CurrencySelectOption {
  return { label: currency, value: currency };
}
